import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

export const Route = createFileRoute('/volleyball-league-software')({
    component: RouteComponent,
})

const FEATURES = [
    {code: "ST", title: "Set-by-set scoring", desc: "Record every set to 25, with a deciding set to 15, exactly as it was played."},
    {code: "PT", title: "Official 3-2-1-0 points", desc: "League points awarded by the scoreline: 3-0 and 3-1 wins are worth more than a 3-2."},
    {code: "SR", title: "Set ratio tiebreaks", desc: "Standings separate level teams on sets won and lost, not just matches."},
    {code: "FX", title: "Weekly fixtures", desc: "Schedule match days across courts, halls and divisions."},
    {code: "DV", title: "Divisions", desc: "Run men's, women's, mixed and graded divisions side by side."},
    {code: "TB", title: "Live standings", desc: "Publish a table that updates the moment a result is submitted."},
];

const LEAGUE_TYPES = [
    {name: "League Season", sub: "Everyone plays everyone", to: "/round-robin-tournament-generator/"},
    {name: "League & Play-offs", sub: "Regular season into knockout", to: "/group-and-knockout-generator/"},
    {name: "Volleyball Tournaments", sub: "Pools and knockout brackets", to: "/volleyball-tournament-software/"},
    {name: "Sports League Management", sub: "Recurring fixtures and standings", to: "/sports-league-management-software/"},
];

const BENEFITS = [
    {title: "Scores stored properly", desc: "A 3-2 win reads as 25-21 23-25 25-19 22-25 15-12, not as a single number."},
    {title: "Tables that follow the rules", desc: "Match points, set ratio and point ratio are worked out for you."},
    {title: "Everyone sees the same table", desc: "Captains and players follow fixtures and standings from a public link."},
];

const USE_CASES = [
    {title: "For Volleyball Clubs", desc: "Run weekly league nights across teams and divisions."},
    {title: "For Regional Leagues", desc: "Manage several divisions of clubs through a full season."},
    {title: "For University Leagues", desc: "Keep intramural and inter-university fixtures organised."},
    {title: "For Beach Volleyball", desc: "Run recurring pairs competitions across courts."},
    {title: "For Mixed Leagues", desc: "Separate men's, women's and mixed divisions cleanly."},
    {title: "For Junior Volleyball", desc: "Run age-group leagues with their own tables."},
];

const COMPARE = [
    {need: "Set scores", manual: "Only the match result recorded", scorepal: "Every set stored, including the decider"},
    {need: "League points", manual: "3-2 wins scored the same as 3-0", scorepal: "Official 3-2-1-0 points applied automatically"},
    {need: "Tiebreaks", manual: "Set ratio worked out by hand", scorepal: "Sets and points ratio calculated for you"},
    {need: "Fixtures", manual: "Match days emailed round as a document", scorepal: "Scheduled across courts and shared as a link"},
    {need: "Player experience", manual: "Table posted days later", scorepal: "Standings update as results come in"},
];

const FAQS = [
    {
        q: 'What is volleyball league software?',
        a: <>Volleyball league software helps clubs and regional organisers schedule match days, record set scores, apply league points and publish standings across a season without maintaining spreadsheets.</>,
    },
    {
        q: 'Does ScorePal use the official 3-2-1-0 points system?',
        a: <>Yes. A 3-0 or 3-1 win is worth three points, a 3-2 win two points, a 2-3 loss one point and a 0-3 or 1-3 loss none — applied automatically from the set scores.</>,
    },
    {
        q: 'Can scores be recorded set by set?',
        a: <>Yes. Matches are entered set by set, to 25 with a deciding set to 15, so the full scoreline is kept and set ratio tiebreaks work correctly.</>,
    },
    {
        q: 'Can ScorePal run multiple divisions?',
        a: <>Yes. Men's, women's, mixed, graded and junior divisions each run as their own competition with their own fixtures and table.</>,
    },
    {
        q: 'Is this different from volleyball tournament software?',
        a: <>Yes. Volleyball league software is built for a season of recurring match days and a table. <Link to={"/volleyball-tournament-software/" as any} className="text-[#082F8A] underline font-semibold">Volleyball tournament software</Link> is built for one-day events with pools and knockout brackets.</>,
    },
];

function RouteComponent() {
    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Volleyball League Software for Clubs & Organisers | ScorePal</title>
            <meta name="description" content="Run volleyball leagues with ScorePal. Schedule match days, record set-by-set scores, apply official 3-2-1-0 league points and publish standings with set ratio tiebreaks." />
            <link rel="canonical" href={`${config.publicUrl}/volleyball-league-software/`} />
            <meta property="og:title" content="Volleyball League Software | ScorePal" />
            <meta property="og:description" content="Create volleyball league fixtures, record set scores and publish official standings with ScorePal." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/volleyball-league-software/`} />
            <meta property="og:image" content="https://scorepal.co/images/volleyball-league-software-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Volleyball League Software | ScorePal" />
            <meta name="twitter:description" content="Run volleyball league seasons, set scores and standings from one platform." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "ScorePal Volleyball League Software",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "Volleyball league software for scheduling match days, recording set scores, applying 3-2-1-0 league points and publishing standings.",
                "url": "https://scorepal.co/volleyball-league-software/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is volleyball league software?", "acceptedAnswer": {"@type": "Answer", "text": "Volleyball league software helps clubs and regional organisers schedule match days, record set scores, apply league points and publish standings across a season without maintaining spreadsheets."}},
                    {"@type": "Question", "name": "Does ScorePal use the official 3-2-1-0 points system?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. A 3-0 or 3-1 win is worth three points, a 3-2 win two points, a 2-3 loss one point and a 0-3 or 1-3 loss none, applied automatically from the set scores."}},
                    {"@type": "Question", "name": "Can scores be recorded set by set?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Matches are entered set by set, to 25 with a deciding set to 15, so the full scoreline is kept and set ratio tiebreaks work correctly."}},
                    {"@type": "Question", "name": "Can ScorePal run multiple divisions?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Men's, women's, mixed, graded and junior divisions each run as their own competition with their own fixtures and table."}},
                    {"@type": "Question", "name": "Is this different from volleyball tournament software?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Volleyball league software is built for a season of recurring match days and a table, while volleyball tournament software is built for one-day events with pools and knockout brackets."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Volleyball League Software", item: "https://scorepal.co/volleyball-league-software/"}]} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Volleyball League Software
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(38px,5.5vw,68px)] leading-none font-bold tracking-wide mb-5">
                                    Run volleyball leagues with real set scores and proper standings
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    ScorePal schedules your match days, records every set to 25 with a decider to 15, and
                                    applies the official 3-2-1-0 league points — so a hard-fought 3-2 counts for exactly
                                    what it should.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Get started - it's free</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/volleyball-tournament-software/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Running a Tournament?
                                    </Link>
                                </div>
                            </div>

                            {/* Live league table card */}
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-sm">
                                <h3 className="font-bebas text-white text-2xl font-bold tracking-wide mb-2">Live volleyball standings</h3>
                                <p className="font-inter text-white/70 text-sm mb-5">League points and set ratio update as each match day is submitted.</p>
                                <div className="rounded-2xl bg-white/10 p-3">
                                    <div className="grid grid-cols-[28px_1fr_52px_44px] items-center gap-2 pb-2 border-b border-white/15 font-inter text-white/50 text-xs uppercase tracking-wide">
                                        <span>#</span><span>Team</span><span className="text-center">Sets</span><span className="text-right">Pts</span>
                                    </div>
                                    {[
                                        {pos: 1, team: "Coastal Spikers", sets: "24-9", pts: "29"},
                                        {pos: 2, team: "City Blockers", sets: "21-13", pts: "25"},
                                        {pos: 3, team: "Harbour VC", sets: "16-18", pts: "18"},
                                        {pos: 4, team: "Riverside Aces", sets: "11-22", pts: "11"},
                                    ].map((row, i, arr) => (
                                        <div key={row.pos} className={`grid grid-cols-[28px_1fr_52px_44px] items-center gap-2 py-3 ${i < arr.length - 1 ? "border-b border-white/10" : ""}`}>
                                            <span className="font-inter text-white/60 text-sm">{row.pos}</span>
                                            <strong className="font-inter text-white text-sm">{row.team}</strong>
                                            <span className="font-inter text-white/70 text-sm text-center">{row.sets}</span>
                                            <strong className="font-inter text-white text-sm text-right">{row.pts}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Everything you need to run a volleyball league
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Built around how volleyball is actually scored, so the table reflects the sport rather than a simplified win column.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <div className="w-11 h-11 rounded-xl bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{f.code}</div>
                                <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-2">{f.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* League types */}
            <section id="league-types" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Support for every type of volleyball competition
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        A straight round-robin season, a regular season that finishes with play-offs, or a one-day pools event — ScorePal handles the structure.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {LEAGUE_TYPES.map((f) => (
                            <Link key={f.name} to={f.to as any} className="group bg-white border border-[#e5e7eb] rounded-2xl p-5 hover:-translate-y-1 hover:border-[#082F8A]/25 transition">
                                <div className="font-inter font-bold text-[#1a1a2e] group-hover:text-[#082F8A]">{f.name}</div>
                                <div className="font-inter text-[#1a1a2e]/60 text-sm mt-1">{f.sub}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Season + mock app */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                            A table that respects a five-set match
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 text-base leading-relaxed mb-6">
                            Volleyball standings are not just wins and losses. Losing 2-3 earns a point, winning 3-2 earns
                            two, and level teams are separated on set ratio. ScorePal applies all of it from the set scores
                            your captains enter.
                        </p>
                        <div className="space-y-4">
                            {BENEFITS.map((b) => (
                                <div key={b.title} className="flex gap-3">
                                    <CheckBadgeIcon size={24} />
                                    <div>
                                        <strong className="font-inter text-[#1a1a2e]">{b.title}</strong>
                                        <p className="font-inter text-[#1a1a2e]/70 text-sm">{b.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mock app */}
                    <div className="rounded-3xl bg-linear-to-br from-[#eef4ff] to-white border border-[#e5e7eb] p-6 lg:p-8 shadow-sm">
                        <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5">
                            {[
                                {l: "Division 1 — Match Week 5", r: "In progress", badge: "live"},
                                {l: "Next match", r: "Coastal Spikers vs Harbour VC"},
                                {l: "Court", r: "Court 1 · Wednesday 20:00"},
                                {l: "Last result", r: "25-21 23-25 25-19 25-22"},
                                {l: "Top of the table", r: "Coastal Spikers", badge: "gold"},
                            ].map((row, i, arr) => (
                                <div key={row.l} className={`flex items-center justify-between gap-3 py-3.5 font-inter text-sm ${i < arr.length - 1 ? "border-b border-[#eef0f4]" : ""}`}>
                                    <span className="text-[#1a1a2e]/70">{row.l}</span>
                                    {row.badge === "live"
                                        ? <span className="rounded-full bg-[#ecfdf3] text-[#12b76a] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                        : row.badge === "gold"
                                            ? <span className="rounded-full bg-[#fffbeb] text-[#b54708] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                            : <strong className="text-[#1a1a2e] text-right">{row.r}</strong>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section id="comparison" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        ScorePal vs spreadsheets for volleyball leagues
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        Spreadsheets rarely model 3-2-1-0 points or set ratio properly — so the table drifts from what the rules actually say.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[680px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Need</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Spreadsheets</div>
                                <div className="p-5 text-[#082F8A]">ScorePal</div>
                            </div>
                            {COMPARE.map((row, i) => (
                                <div key={row.need} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] font-semibold">{row.need}</div>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/70">{row.manual}</div>
                                    <div className="p-5 font-medium">{row.scorepal}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Use cases */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Built for clubs, regional leagues and university volleyball
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        From a single club night to a multi-division regional league, ScorePal keeps fixtures, set scores and standings in order.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {USE_CASES.map((u) => (
                            <div key={u.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{u.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{u.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA card → tournament software */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="rounded-3xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
                    <div>
                        <h2 className="font-bebas text-white uppercase text-[clamp(26px,3.5vw,40px)] leading-tight font-bold tracking-wide mb-4">
                            Running a one-day volleyball tournament?
                        </h2>
                        <p className="font-inter text-white/80 text-base leading-relaxed mb-6">
                            This page is about league seasons. If you are running pools into knockout rounds across a
                            single day or weekend, see our volleyball tournament software page.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to={"/volleyball-tournament-software/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                                <span className="font-inter text-base font-medium">View Tournament Software</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link to={"/signup/player/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                Get started - it's free
                            </Link>
                        </div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                        <h3 className="font-inter text-white font-semibold mb-3">Popular tournament features</h3>
                        <ul className="list-disc pl-5 space-y-1.5 font-inter text-sm text-white/85">
                            <li>Pool play</li>
                            <li>Knockout brackets</li>
                            <li>Court scheduling</li>
                            <li>Graded divisions</li>
                            <li>Finals scheduling</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="container mx-auto px-6 lg:px-12 pb-8 lg:pb-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run your next volleyball league with ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Match days, set-by-set scores, official 3-2-1-0 points and live standings — volleyball league software built for clubs and regional organisers.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Set Scoring", "3-2-1-0 Points", "Set Ratio", "Weekly Fixtures", "Divisions", "Live Standings"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Volleyball League Software FAQs"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
